// src/features/executive/insight/widgets/CategoricalDistributionCard.tsx
import { useMemo } from 'react'
import CategoricalDonut, { CategoricalDonutProps } from './CategoricalDonut'
import CategoricalBar, { CategoricalBarRow } from './CategoricalBar'
import { BAR_THRESHOLD, shouldUseBar, sortByValueDesc } from 'features/executive/shared/utils/chart-mode'
import { formatCount, formatVariance } from 'utils/executive.utils'

export type CategoricalDistributionCardProps = CategoricalDonutProps & {
  // Categories above this count switch to a ranked bar list. Default 5.
  barThreshold?: number
}

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

// Renders a donut for small distributions and a ranked bar list (top 5 +
// "show all") once the category count passes the threshold. Drop-in for the
// count-based insight donuts — accepts the full CategoricalDonut prop set.
export default function CategoricalDistributionCard(props: CategoricalDistributionCardProps) {
  const {
    barThreshold = BAR_THRESHOLD,
    segments,
    total,
    colorMap,
    unit,
    previousTotal,
    ...rest
  } = props

  const useBar = shouldUseBar(segments.length, barThreshold)

  const rows: CategoricalBarRow[] = useMemo(
    () =>
      sortByValueDesc(segments, (s) => s.count).map((s) => ({
        key: s.key,
        label: s.label,
        count: s.count,
        percent: s.percent,
        previousCount: s.previousCount,
        color: colorMap[s.key] ?? '#95a5a6',
      })),
    [segments, colorMap],
  )

  if (!useBar) {
    return <CategoricalDonut {...props} />
  }

  const totalVariance = varianceOf(total, previousTotal)
  const subtitle = `${props.centerLabel ? `${props.centerLabel} ` : ''}${formatCount(total, unit ?? '')}${
    totalVariance != null ? ` · ${formatVariance(totalVariance)}` : ''
  }`

  return (
    <CategoricalBar
      title={rest.title}
      titleTooltip={rest.titleTooltip}
      notice={rest.notice}
      emptyMessage={rest.emptyMessage}
      subtitle={subtitle}
      rows={rows}
      initialVisible={barThreshold}
      formatValue={(r) => `${formatCount(r.count, unit ?? '')} (${r.percent.toFixed(1)}%)`}
      loading={rest.loading}
      error={rest.error}
      onRetry={rest.onRetry}
    />
  )
}
